import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Search, X, Loader2, Truck, IndianRupee } from "lucide-react";
import { api, fmt, fmtDate, apiError } from "@/lib/api";
import { StatusChip } from "@/pages/OrdersPage";

const STATUSES = ["", "confirmed", "packed", "dispatched", "in_transit", "out_for_delivery", "delivered", "cancelled"];
const STATUS_FLOW = ["confirmed", "packed", "dispatched", "in_transit", "out_for_delivery", "delivered"];
const COD_METHODS = [{ id: "cash", label: "Cash" }, { id: "upi", label: "UPI" }, { id: "other", label: "Other" }];
const DELIVERY_PARTNERS = [
  { id: "self_fleet", label: "Self fleet" }, { id: "porter", label: "Porter" },
  { id: "dunzo", label: "Dunzo" }, { id: "delhivery", label: "Delhivery" },
  { id: "bluedart", label: "BlueDart" }, { id: "dtdc", label: "DTDC" }, { id: "other", label: "Other" },
];

const ADMIN_INPUT = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

function DeliveryCosts({ order }) {
  const [entries, setEntries] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ amount: "", partner: "self_fleet",
    cost_date: new Date().toISOString().slice(0, 10), reference: order.tracking_id || "", note: "" });

  const load = useCallback(() => {
    api.get(`/admin/orders/${order.id}/delivery-costs`)
      .then(({ data }) => setEntries(data.entries))
      .catch(() => setEntries([]));
  }, [order.id]);
  useEffect(load, [load]);

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid positive amount"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/delivery-costs`, {
        amount, partner: form.partner, cost_date: form.cost_date,
        reference: form.reference, note: form.note,
      });
      toast.success("Delivery cost recorded");
      setForm((f) => ({ ...f, amount: "", note: "" }));
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't record the delivery cost"));
    } finally {
      setBusy(false);
    }
  };

  const total = (entries || []).reduce((n, e) => n + e.amount, 0);

  return (
    <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.04] p-4" data-testid="delivery-costs">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-sky-300">
          <Truck className="h-4 w-4" /> Delivery Costs
        </p>
        <p className="text-xs text-neutral-400">total <strong className="text-white" data-testid="dc-total">{fmt(total)}</strong> · {(entries || []).length} {((entries || []).length) === 1 ? "entry" : "entries"}</p>
      </div>
      {(entries || []).length > 0 && (
        <div className="mt-2 space-y-1" data-testid="dc-history">
          {entries.map((e) => (
            <p key={e.id} className="text-[11px] text-neutral-500">
              {fmt(e.amount)} · {e.cost_date} · {e.partner.replace(/_/g, " ")}{e.reference && ` · ref ${e.reference}`} · by {e.created_by}
            </p>
          ))}
        </div>
      )}
      <div className="mt-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="0.01" step="0.01" value={form.amount} data-testid="dc-amount"
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={ADMIN_INPUT} placeholder="Cost ₹" />
          <input type="date" value={form.cost_date} data-testid="dc-date"
            onChange={(e) => setForm((f) => ({ ...f, cost_date: e.target.value }))} className={ADMIN_INPUT} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.partner} data-testid="dc-partner"
            onChange={(e) => setForm((f) => ({ ...f, partner: e.target.value }))} className={ADMIN_INPUT}>
            {DELIVERY_PARTNERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <input value={form.reference} placeholder="Shipment/tracking ref" data-testid="dc-reference"
            onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className={ADMIN_INPUT} />
        </div>
        <input value={form.note} placeholder="Note (optional)" data-testid="dc-note"
          onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className={ADMIN_INPUT} />
        <button onClick={submit} disabled={busy} data-testid="dc-submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-sky-500 py-2.5 text-xs font-bold text-black transition-colors hover:bg-sky-400 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Add delivery cost{form.amount ? ` · ${fmt(parseFloat(form.amount) || 0)}` : ""}
        </button>
      </div>
    </div>
  );
}

function CodCollection({ order, onUpdated }) {
  const [form, setForm] = useState({ amount: String(order.total), collection_date: new Date().toISOString().slice(0, 10), method: "cash", reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.get(`/admin/orders/${order.id}/cod-collections`)
      .then(({ data }) => {
        setHistory(data.collections);
        const collected = data.collections.reduce((n, c) => n + c.amount, 0);
        setForm((f) => ({ ...f, amount: String(Math.max(0, Math.round((order.total - collected) * 100) / 100)) }));
      })
      .catch(() => setHistory([]));
  }, [order.id, order.total]);

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/cod-collection`, {
        amount, collection_date: form.collection_date, method: form.method,
        reference: form.reference, note: form.note,
      });
      toast.success(data.outstanding > 0
        ? `Collected ${fmt(data.collection.amount)} — ${fmt(data.outstanding)} still due`
        : `Fully collected — order ${order.order_number} is paid`);
      setHistory((h) => [...(h || []), data.collection]);
      onUpdated();
    } catch (e) {
      toast.error(apiError(e, "Couldn't record the collection"));
    } finally {
      setBusy(false);
    }
  };

  const collected = (history || []).reduce((n, c) => n + c.amount, 0);
  const outstanding = Math.max(0, Math.round((order.total - collected) * 100) / 100);
  const input = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

  return (
    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid="cod-collection">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-300">
          <IndianRupee className="h-4 w-4" /> COD Collection
        </p>
        <p className="text-xs text-neutral-400">
          due <strong className="text-white" data-testid="cod-outstanding">{fmt(outstanding)}</strong> of {fmt(order.total)}
        </p>
      </div>
      {(history || []).length > 0 && (
        <div className="mt-2 space-y-1" data-testid="cod-history">
          {history.map((c) => (
            <p key={c.id} className="text-[11px] text-neutral-500">
              {fmt(c.amount)} · {c.collection_date} · {c.method}{c.reference && ` · ref ${c.reference}`} · by {c.created_by}
            </p>
          ))}
        </div>
      )}
      {outstanding > 0 && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input type="number" min="0.01" step="0.01" value={form.amount} data-testid="cod-amount"
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className={input} placeholder="Amount ₹" />
            <input type="date" value={form.collection_date} data-testid="cod-date"
              onChange={(e) => setForm((f) => ({ ...f, collection_date: e.target.value }))} className={input} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.method} data-testid="cod-method"
              onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))} className={input}>
              {COD_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <input value={form.reference} placeholder="Reference (optional)" data-testid="cod-reference"
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className={input} />
          </div>
          <input value={form.note} placeholder="Note (optional)" data-testid="cod-note"
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className={input} />
          <button onClick={submit} disabled={busy} data-testid="cod-submit"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-bold text-black transition-colors hover:bg-emerald-400 disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Mark COD collected{form.amount ? ` · ${fmt(parseFloat(form.amount) || 0)}` : ""}
          </button>
        </div>
      )}
    </div>
  );
}

function OrderPanel({ order, onClose, onUpdated }) {
  const [status, setStatus] = useState(order.status);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const update = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}/status`, { status, note });
      toast.success(`Order ${order.order_number} → ${status.replace(/_/g, " ")}`);
      onUpdated();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update order"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" />
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        className="fixed right-0 top-0 z-[85] h-full w-full max-w-lg overflow-y-auto border-l border-white/10 bg-[#F1EDE3] p-7"
        data-lenis-prevent
        data-testid="admin-order-panel"
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="font-display font-mono text-xl font-semibold text-amber-400">{order.order_number}</h3>
            <p className="text-xs text-neutral-500">{fmtDate(order.created_at)}</p>
          </div>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" aria-label="Close" data-testid="admin-order-panel-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">Customer</p>
            <p className="text-sm font-semibold text-white">{order.contact.name}</p>
            <p className="text-xs text-neutral-500">{order.contact.email} · {order.contact.mobile}</p>
            <p className="mt-2 text-xs text-neutral-400">
              {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city}, {order.address.state} — {order.address.pin}
            </p>
          </div>

          <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">Items</p>
            {order.items.map((i) => (
              <div key={i.sku} className="mb-2 flex items-center gap-3">
                <img src={i.image} alt="" className="h-9 w-9 rounded-lg object-cover" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-white">{i.name}</p>
                  <p className="text-[11px] text-neutral-500">{i.variant_label} × {i.qty}</p>
                </div>
                <span className="text-xs font-bold">{fmt(i.line_total)}</span>
              </div>
            ))}
            <div className="mt-3 flex justify-between border-t border-white/[0.07] pt-3 text-sm">
              <span className="text-neutral-400">Total</span>
              <span className="font-display font-semibold text-amber-500">{fmt(order.total)}</span>
            </div>
            <p className="text-[11px] text-neutral-600">
              Payment: {order.payment.method.toUpperCase()} · {order.payment.status === "paid" ? "Paid" : "COD pending"} · {order.payment.payment_id}
            </p>
          </div>

          {order.payment.method === "cod" && order.status === "delivered" && order.payment.status !== "paid" && (
            <CodCollection order={order} onUpdated={onUpdated} />
          )}

          {order.status !== "cancelled" && <DeliveryCosts order={order} />}

          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-amber-500" />
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400">Shipment</p>
            </div>
            <p className="mt-2 font-mono text-sm font-bold text-amber-400">{order.tracking_id || "Not assigned"}</p>
            <p className="text-[11px] text-neutral-500">{order.shipment?.status} · {order.shipment?.current_location}</p>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">Update Fulfillment Status</p>
            <select
              data-testid="admin-order-status-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-12 w-full rounded-xl border border-white/10 bg-[#FFFEFA] px-4 text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none"
            >
              {STATUS_FLOW.concat(order.status === "cancelled" ? ["cancelled"] : ["cancelled"]).map((s, i) => (
                <option key={`${s}-${i}`} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
            <input
              data-testid="admin-order-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional) — e.g. Handed to BlueDart line-haul"
              className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none"
            />
            <button
              data-testid="admin-order-update-btn"
              onClick={update}
              disabled={saving || status === order.status}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Status
            </button>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">History</p>
            {order.fulfillment.slice().reverse().map((f, i) => (
              <div key={i} className="mb-1.5 flex items-baseline justify-between gap-3 text-xs">
                <span className="font-bold capitalize text-neutral-300">{f.status.replace(/_/g, " ")}</span>
                <span className="text-neutral-600">{fmtDate(f.at)}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.aside>
    </>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/orders", { params: { status, q } })
      .then(({ data }) => setOrders(data))
      .catch(() => toast.error("Couldn't load orders"));
  }, [status, q]);
  useEffect(load, [load]);

  return (
    <div data-testid="admin-orders">
      <div className="mb-5 flex flex-wrap gap-3">
        <div className="flex flex-1 items-center gap-3 rounded-xl border border-white/10 bg-black/40 px-4">
          <Search className="h-4 w-4 shrink-0 text-neutral-500" />
          <input data-testid="admin-orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, customer, tracking ID…" className="h-11 w-full bg-transparent text-sm text-white placeholder:text-neutral-600 focus:outline-none" />
        </div>
        <select
          data-testid="admin-orders-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-11 rounded-xl border border-white/10 bg-[#FFFEFA] px-4 text-xs font-bold text-neutral-300 focus:border-amber-500/60 focus:outline-none"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "All Statuses"}</option>
          ))}
        </select>
      </div>

      {!orders ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] py-16 text-center text-sm text-neutral-500" data-testid="admin-orders-empty">
          No orders match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                <th className="px-5 py-4">Order</th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">Items</th>
                <th className="px-5 py-4">Total</th>
                <th className="px-5 py-4">Payment</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Tracking</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  className="cursor-pointer border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]"
                  onClick={() => setSelected(o)}
                  data-testid={`admin-order-row-${o.order_number}`}
                >
                  <td className="px-5 py-4 font-mono font-bold text-amber-400">{o.order_number}</td>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-white">{o.contact.name}</p>
                    <p className="text-[11px] text-neutral-500">{o.contact.email}</p>
                  </td>
                  <td className="px-5 py-4 text-neutral-400">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                  <td className="px-5 py-4 font-semibold text-white">{fmt(o.total)}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-bold uppercase ${o.payment.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>
                      {o.payment.status === "paid" ? "Paid" : "COD"}
                    </span>
                  </td>
                  <td className="px-5 py-4"><StatusChip status={o.status} /></td>
                  <td className="px-5 py-4 font-mono text-xs text-neutral-400">{o.tracking_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <OrderPanel
            order={selected}
            onClose={() => setSelected(null)}
            onUpdated={() => { setSelected(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
